/* eslint-disable indent */
"use client";

import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import BreadcrumbsComponent from "components/atoms/Breadcrumbs";
import { ErrorMessage } from "components/atoms/Input/Input.styled";
import CreateFolderModal from "components/molecules/CreateFolderModal";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import AppBarComponent from "components/molecules/FolderAppBar";
import FolderList from "components/molecules/FolderList";
import { ImageGallery } from "components/organisms/ImageGallery";
import { createFolder, deleteFolder, fetchFolderById, fetchFoldersByUser, getUserInfo, updateFolder } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, FolderDTO, UserPremiumType } from "types";
import { Title } from "utils/styling/typography.styled";
import * as Styled from "./MyGalleryLanding.styled";
export const MyGalleryLanding: FC = () => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folders, setFolders] = useState<FolderDTO[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<{
    id: string | null;
    name: string;
  }[]>([{
    id: null,
    name: "Home"
  }]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isRenameFolderOpen, setIsRenameFolderOpen] = useState(false);
  const [folderToRename, setFolderToRename] = useState<FolderDTO | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null);
  const hasCheckedLogin = useRef(false);
  useEffect(() => {
    const checkUserAndLoadFolders = async () => {
      if (!hasCheckedLogin.current) {
        const {
          user
        } = await getUserInfo();
        if (!user) {
          if (!hasCheckedLogin.current) {
            dispatch({
              type: AppReducerActionType.SHOW_AUTH_MODAL,
              payload: {
                ...state,
                showAuthModal: true
              }
            });
            hasCheckedLogin.current = true; // Set the flag to prevent re-dispatching
            return;
          }
        } else {
          hasCheckedLogin.current = true;
          loadFolderContents();
        }
      } else {
        loadFolderContents();
      }
    };
    checkUserAndLoadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId, state, dispatch]);
  useEffect(() => {
    let isMounted = true;
    const updateBreadcrumbs = async () => {
      if (currentFolderId) {
        const breadcrumbs = await buildBreadcrumbs(currentFolderId);
        if (isMounted) {
          setBreadcrumbs(breadcrumbs);
        }
      } else {
        setBreadcrumbs([{
          id: null,
          name: "Home"
        }]);
      }
    };
    updateBreadcrumbs();
    return () => {
      isMounted = false;
    };
  }, [currentFolderId]);
  const loadFolderContents = async () => {
    setIsLoading(true);
    try {
      let data;
      if (!currentFolderId) {
        data = await fetchFoldersByUser();
        setFolders(data as FolderDTO[] || []);
        setError(null);
      } else {
        data = await fetchFolderById(currentFolderId);
        setFolders((data as FolderDTO).subFolders || []);
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load folder contents");
      setFolders([]);
    } finally {
      setIsLoading(false);
    }
  };
  const buildBreadcrumbs = async (folderId: string): Promise<{
    id: string | null;
    name: string;
  }[]> => {
    try {
      let currentId: string | null = folderId;
      const path: {
        id: string | null;
        name: string;
      }[] = [];
      while (currentId) {
        const response = await fetchFolderById(currentId);
        if (response.error) {
          setError(response.error);
          break;
        }
        if (response as FolderDTO) {
          const folder = response as FolderDTO;
          path.unshift({
            id: folder.id,
            name: folder.name
          });
          currentId = folder.parentFolderId || null;
        } else {
          break;
        }
      }
      path.unshift({
        id: null,
        name: "Home"
      });
      return path; // Return the breadcrumbs array
    } catch (err: any) {
      setError(err.message || "Failed to build breadcrumbs");
      return [{
        id: null,
        name: "Home"
      }]; // Return a default value or rethrow the error
    }
  };
  const handleCreateFolder = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setIsCreateFolderModalOpen(true);
  };
  const handleRenameFolder = (folder: FolderDTO) => {
    setIsRenameFolderOpen(true);
    setFolderToRename(folder);
  };
  const handleConfirmDelete = async () => {
    setShowDeleteConfirmation(false);
    try {
      if (currentFolderId) {
        const deleteResponse = await deleteFolder(currentFolderId);
        if (!deleteResponse.success) {
          throw new Error(deleteResponse.error || "Failed to delete folder");
        }
        // Navigate to parent folder
        const parent = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2] : {
          id: null,
          name: "Home"
        };
        setCurrentFolderId(parent.id);
      } else if (folderToDelete) {
        const deleteResponse = await deleteFolder(folderToDelete);
        if (!deleteResponse.success) {
          throw new Error(deleteResponse.error || "Failed to delete folder");
        }
        loadFolderContents();
      } else {
        throw new Error("No folder selected to delete");
      }
    } catch (err: any) {
      setError(err.message || "Failed to delete folder");
    }
  };
  const handleCreateFolderSubmit = async (folderName: string) => {
    if (folderName && folderName.trim() !== "") {
      try {
        const response = await createFolder({
          name: folderName.trim(),
          parentFolderId: currentFolderId
        });
        if (response.error) {
          setError(response.error);
          throw new Error(response.error);
        }
        setIsCreateFolderModalOpen(false);
        loadFolderContents();
      } catch (err: any) {
        setError(err.message || "Failed to create folder");
      }
    }
  };
  const handleRenameFolderSubmit = async (folderName: string) => {
    if (folderToRename && folderName && folderName.trim() !== "") {
      try {
        const response = await updateFolder(folderToRename.id, {
          name: folderName.trim(),
          description: folderToRename.description
        });
        if (response.error) {
          setError(response.error);
          throw new Error(response.error);
        }
        setIsRenameFolderOpen(false);
        loadFolderContents();
      } catch (err: any) {
        setError(err.message || "Failed to rename folder");
      }
    }
  };
  const handleDeleteFolder = async () => {
    if (!currentFolderId) return;
    setShowDeleteConfirmation(true);
  };
  const handleDeleteFolderItem = async (folder: FolderDTO) => {
    setFolderToDelete(folder.id);
    setShowDeleteConfirmation(true);
  };
  const handleFolderClick = (folder: FolderDTO) => {
    setCurrentFolderId(folder.id);
  };
  const handleBreadcrumbClick = (folderId: string | null) => {
    setCurrentFolderId(folderId);
  };
  const defaultFilters = useMemo(() => ({
    folderId: currentFolderId ?? ""
  }), [currentFolderId]);
  return <Styled.Main data-sentry-element="unknown" data-sentry-component="MyGalleryLanding" data-sentry-source-file="index.tsx">
            <Styled.AboutSection data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Title data-sentry-element="Title" data-sentry-source-file="index.tsx">
                    My D&D Gallery: AI-Generated Characters, NPCs, and Monsters
                </Title>
            </Styled.AboutSection>
            <Styled.GalleryWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <AppBarComponent currentFolderId={currentFolderId} onCreateFolder={handleCreateFolder} onDeleteFolder={handleDeleteFolder} data-sentry-element="AppBarComponent" data-sentry-source-file="index.tsx" />
                {state.isLoggedIn && state.userPremiumLevel === UserPremiumType.NONE && <Styled.SubscriberInfo>
                            Folder Management is a{" "}
                            <Styled.SubscriberPerk>
                                Subscriber Perk
                            </Styled.SubscriberPerk>
                            . Upgrade your account to unlock this feature.
                        </Styled.SubscriberInfo>}
                <BreadcrumbsComponent breadcrumbs={breadcrumbs} onBreadcrumbClick={handleBreadcrumbClick} data-sentry-element="BreadcrumbsComponent" data-sentry-source-file="index.tsx" />
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {isLoading ? <Styled.LoadingIndicator>
                        Loading...
                    </Styled.LoadingIndicator> : <FolderList folders={folders} onFolderClick={handleFolderClick} onRenameFolder={handleRenameFolder} onDeleteFolder={handleDeleteFolderItem} />}
            </Styled.GalleryWrapper>

            {state.isLoggedIn && <Styled.ImageGalleryWrapper>
                    <ImageGallery gallerySubtitle="Recent Generations: " userGallery={true} defaultFilters={defaultFilters} folderId={currentFolderId} />
                </Styled.ImageGalleryWrapper>}
            <CreateFolderModal isOpen={isCreateFolderModalOpen} onClose={() => setIsCreateFolderModalOpen(false)} onSubmit={handleCreateFolderSubmit} text="Create New Folder" data-sentry-element="CreateFolderModal" data-sentry-source-file="index.tsx" />
            <CreateFolderModal isOpen={isRenameFolderOpen} onClose={() => setIsRenameFolderOpen(false)} onSubmit={handleRenameFolderSubmit} text="Rename Folder" data-sentry-element="CreateFolderModal" data-sentry-source-file="index.tsx" />
            <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={() => setShowDeleteConfirmation(false)} text="folder" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
        </Styled.Main>;
};